'use client';

import ProfileClient from '@/components/profile/profile-client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useProfileModal } from '@/contexts/profile-modal-context';

/**
 * Global host for the in-app profile modal. Mounted once at the provider
 * tree root so any surface (`AppBottomNav`, activity pills, holder lists,
 * etc.) can open the profile by calling `openProfile(address)` without
 * each caller having to allocate its own Radix dialog.
 *
 * Sizing:
 *   • `<sm` (mobile): full-screen modal so the profile feels like a
 *     first-class view rather than a cramped sheet. The caller's bottom
 *     nav is overlaid, not double-mounted, because Radix moves the
 *     modal into a portal outside the nav's stacking context.
 *   • `sm+` (tablet/desktop): classic centered 960px dialog with a
 *     generous max-height so activity/tiles tabs can scroll inside it
 *     without the modal itself growing past the viewport.
 */
export function ProfileModalHost() {
  const { profileAddress, closeProfile } = useProfileModal();
  const open = !!profileAddress;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeProfile()}>
      <DialogContent
        hideClose
        className="w-screen h-[100dvh] max-w-none max-h-none rounded-none border-0 sm:w-[min(95vw,960px)] sm:max-w-[960px] sm:h-auto sm:max-h-[min(92vh,900px)] sm:rounded-md sm:border sm:border-amber-500/30 overflow-hidden bg-[#0b0a08] p-0 gap-0 text-zinc-100 shadow-[0_30px_120px_-40px_rgba(0,0,0,0.9)] flex flex-col"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Profile</DialogTitle>
        </DialogHeader>
        {profileAddress ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
            <ProfileClient
              addressOverride={profileAddress}
              embedded
              onClose={closeProfile}
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
